/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { z } from 'zod';
import type { TFunction } from 'i18next';

/**
 * Factory function to create the donation amount validation schema with
 * i18next translations. Returns a schema at runtime (not module scope) to
 * avoid race conditions where t() would be called before i18next is
 * initialized. Mirrors `createPromoCodeFormSchema`.
 *
 * @example const schema = createDonationFormSchema(t);
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any, react-refresh/only-export-components
export const createDonationFormSchema = (t: TFunction<any, any>) => {
    return z.object({
        // Kept as a string (not transformed to number) so the schema's input and
        // output types match `DonationFormData` — the raw field value RHF holds.
        // The numeric amount is derived at submit time in `form.tsx`.
        amount: z.string().refine((val) => val.trim() === '' || (!isNaN(Number(val)) && Number(val) >= 0), {
            message: t('donation.validation.invalidAmount'),
        }),
    });
};

// Type export
export type DonationFormData = z.infer<ReturnType<typeof createDonationFormSchema>>;

// Export main component
export { DonationWidget } from './form';

// Export sub-components
export { DonationFields } from './donation-field';

// Export types
export { type DonationWidgetProps, type DonationFieldsProps, type DonationFetcherData } from './types';

// Default export
export { DonationWidget as default } from './form';
